import { useCallback, type Dispatch, type MutableRefObject, type SetStateAction, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type TouchEvent as ReactTouchEvent } from 'react'
import type { ChecklistItem as BoardChecklistItem } from '../../types/schema'
import type { DragState } from './canvasMisc'
import { isGateNode, MIN_VIEWPORT_WIDTH, MIN_VIEWPORT_HEIGHT } from './boardItem'
import { NODE_CAP_DIAMETER } from '../../services/canvasLayoutConstants'
import { executeDeleteGate } from './gateActions'
import { assertAndSemanticsForPrereqs, type AnchorName } from './wireGeometry'

export interface UseBoardPointerHandlersParams {
  isPlacingNode: boolean
  setIsPlacingNode: Dispatch<SetStateAction<boolean>>
  clientToWorld: (event: ReactPointerEvent | ReactMouseEvent | { clientX: number; clientY: number }) => { x: number; y: number }
  startNewDraftChecklist: () => void
  stopPanMomentum: () => void
  panVelocityRef: MutableRefObject<{ x: number; y: number; t: number }[]>
  dragRef: MutableRefObject<DragState | null>
  canEditCanvas: boolean
  boardRef: MutableRefObject<HTMLDivElement | null>
  getRenderedAnchorPoint: (node: BoardChecklistItem, anchor: AnchorName) => { x: number; y: number }
  setWireDraft: Dispatch<SetStateAction<Extract<DragState, { type: 'wire' }> | null>>
  tooltipTouchTimerRef: MutableRefObject<number | null>
  showChecklistNodeHover: (anchor: Element, node: BoardChecklistItem, parentPath: string, parentColor: string) => void
  hideChecklistHover: () => void
  canDeleteChecklist: boolean
  displayNodes: BoardChecklistItem[]
  boardDocumentIdsRef: MutableRefObject<Set<string>>
  flashLocal: (message: string) => void
  showFlash: (message: string) => void
  pendingDeletionsRef: MutableRefObject<Set<string>>
  commitLocalNodes: (updater: (current: BoardChecklistItem[]) => BoardChecklistItem[]) => void
  captureCursorPresence: () => void
  setOptimisticPositions: Dispatch<SetStateAction<Record<string, { x: number; y: number }>>>
  setViewportSize: Dispatch<SetStateAction<{ width: number; height: number; scrollLeft: number; scrollTop: number }>>
  nodesById: Map<string, BoardChecklistItem>
  resolveDropPosition: (itemId: string, position: { x: number; y: number }) => { position: { x: number; y: number }; displacements: Map<string, { x: number; y: number }> }
  boardScopedNodes: BoardChecklistItem[]
  isCalendarShelfOpen: boolean
  moveNodeToVisionboard: (node: BoardChecklistItem) => Promise<void>
  setSelectedNodeId: Dispatch<SetStateAction<string | null>>
  animateBreakingWire: (source: BoardChecklistItem, target: BoardChecklistItem) => void
  boardScale: number
  panMomentumRafRef: MutableRefObject<number | null>
}

export function useBoardPointerHandlers({
  isPlacingNode,
  setIsPlacingNode,
  clientToWorld,
  startNewDraftChecklist,
  stopPanMomentum,
  panVelocityRef,
  dragRef,
  canEditCanvas,
  boardRef,
  getRenderedAnchorPoint,
  setWireDraft,
  tooltipTouchTimerRef,
  showChecklistNodeHover,
  hideChecklistHover,
  canDeleteChecklist,
  displayNodes,
  boardDocumentIdsRef,
  flashLocal,
  showFlash,
  pendingDeletionsRef,
  commitLocalNodes,
  captureCursorPresence,
  setOptimisticPositions,
  setViewportSize,
  nodesById,
  resolveDropPosition,
  boardScopedNodes,
  isCalendarShelfOpen,
  moveNodeToVisionboard,
  setSelectedNodeId,
  animateBreakingWire,
  boardScale,
  panMomentumRafRef,
}: UseBoardPointerHandlersParams) {
  const handleBoardPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (isPlacingNode) {
      event.preventDefault()
      // Popup-first: don't materialize the node on click, open the details
      // draft drawer instead. Position from the click point is ignored — the
      // drawer's Save uses `getNextChecklistGridPosition()`, matching the
      // toolbar "Add new checklist" behavior.
      void clientToWorld(event)
      setIsPlacingNode(false)
      startNewDraftChecklist()
      return
    }

    stopPanMomentum()
    panVelocityRef.current = [{ x: event.clientX, y: event.clientY, t: performance.now() }]

    dragRef.current = {
      type: 'pan',
      startClientX: event.clientX,
      startClientY: event.clientY,
      startScrollLeft: event.currentTarget.scrollLeft,
      startScrollTop: event.currentTarget.scrollTop,
    }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const handleNodePointerDown = useCallback((event: ReactPointerEvent, node: BoardChecklistItem) => {
    if (!canEditCanvas) return
    event.preventDefault()
    event.stopPropagation()
    const world = clientToWorld(event)
    dragRef.current = {
      type: 'node',
      itemId: node.id,
      startClientX: event.clientX,
      startClientY: event.clientY,
      offsetX: world.x - node.position.x,
      offsetY: world.y - node.position.y,
      didMove: false,
    }
    boardRef.current?.setPointerCapture(event.pointerId)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a3: boardRef and dragRef are useRef identities passed from the Surface (stable)
  }, [canEditCanvas, clientToWorld])

  const handleAnchorPointerDown = useCallback((event: ReactPointerEvent, node: BoardChecklistItem, anchor: AnchorName) => {
    if (!canEditCanvas) return
    event.preventDefault()
    event.stopPropagation()
    const cursor = clientToWorld(event)
    const nextWire = {
      type: 'wire' as const,
      sourceId: node.id,
      sourceAnchor: anchor,
      cursor,
      client: { x: event.clientX, y: event.clientY },
    }
    dragRef.current = nextWire
    setWireDraft(nextWire)
    boardRef.current?.setPointerCapture(event.pointerId)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: setWireDraft are useRef/useState identities passed from the Surface (stable)
  }, [canEditCanvas, clientToWorld])

  const handleBoardNodeMouseEnter = useCallback((
    event: ReactMouseEvent<HTMLElement>,
    node: BoardChecklistItem,
    parentPath: string,
    parentColor: string,
  ) => {
    showChecklistNodeHover(event.currentTarget, node, parentPath, parentColor)
  }, [showChecklistNodeHover])

  const handleBoardNodeMouseMove = useCallback((_event: ReactMouseEvent<HTMLElement>, _node: BoardChecklistItem) => {
    // Anchor tracks the element rect via autoUpdate; no per-pixel state to keep.
  }, [])

  const handleBoardNodeTouchStart = useCallback((
    event: ReactTouchEvent<HTMLElement>,
    node: BoardChecklistItem,
    parentPath: string,
    parentColor: string,
  ) => {
    const anchor = event.currentTarget
    if (tooltipTouchTimerRef.current !== null) window.clearTimeout(tooltipTouchTimerRef.current)
    tooltipTouchTimerRef.current = window.setTimeout(() => {
      showChecklistNodeHover(anchor, node, parentPath, parentColor)
    }, 450)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: tooltipTouchTimerRef are useRef/useState identities passed from the Surface (stable)
  }, [showChecklistNodeHover])

  const clearBoardNodeTooltip = useCallback(() => {
    if (tooltipTouchTimerRef.current !== null) window.clearTimeout(tooltipTouchTimerRef.current)
    tooltipTouchTimerRef.current = null
    hideChecklistHover()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: tooltipTouchTimerRef are useRef/useState identities passed from the Surface (stable)
  }, [hideChecklistHover])

  // Gate deletion handler — connected gates (2-4 inputs AND outgoing wire) cannot be deleted.
  const deleteGateRequest = useCallback((node: BoardChecklistItem) => {
    executeDeleteGate({
      node,
      displayNodes,
      boardDocumentIds: boardDocumentIdsRef.current,
      canDeleteChecklist,
      flashLocal,
      showFlash,
      pendingDeletions: pendingDeletionsRef.current,
      commitLocalNodes,
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: boardDocumentIdsRef, pendingDeletionsRef are useRef/useState identities passed from the Surface (stable)
  }, [canDeleteChecklist, commitLocalNodes, displayNodes, flashLocal, showFlash])

  const deleteOpenGate = deleteGateRequest

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    captureCursorPresence()
    const drag = dragRef.current
    if (!drag) return

    if (drag.type === 'pan') {
      event.currentTarget.scrollLeft = drag.startScrollLeft - (event.clientX - drag.startClientX)
      event.currentTarget.scrollTop = drag.startScrollTop - (event.clientY - drag.startClientY)
      panVelocityRef.current.push({ x: event.clientX, y: event.clientY, t: performance.now() })
      if (panVelocityRef.current.length > 6) panVelocityRef.current.shift()
      return
    }

    if (drag.type === 'node') {
      const didMove = drag.didMove || Math.hypot(event.clientX - drag.startClientX, event.clientY - drag.startClientY) > 5
      if (didMove && !drag.didMove) dragRef.current = { ...drag, didMove: true }
      const world = clientToWorld(event)
      setOptimisticPositions((current) => ({
        ...current,
        [drag.itemId]: {
          x: Math.max(0, world.x - drag.offsetX),
          y: Math.max(0, world.y - drag.offsetY),
        },
      }))
      return
    }

    if (drag.type === 'wire') {
      const nextWire = {
        ...drag,
        cursor: clientToWorld(event),
        client: { x: event.clientX, y: event.clientY },
      }
      dragRef.current = nextWire
      setWireDraft(nextWire)
      return
    }

    if (drag.type === 'resize') {
      const deltaX = event.clientX - drag.startClientX
      const deltaY = event.clientY - drag.startClientY
      const growsRight = drag.edge.includes('right')
      const growsLeft = drag.edge.includes('left')
      const growsBottom = drag.edge.includes('bottom')
      const growsTop = drag.edge.includes('top')
      const maxWidth = boardRef.current?.parentElement?.getBoundingClientRect().width ?? drag.startWidth

      setViewportSize((current) => ({
        ...current,
        width: Math.max(
          MIN_VIEWPORT_WIDTH,
          Math.min(maxWidth, drag.startWidth + (growsRight ? deltaX : 0) - (growsLeft ? deltaX : 0)),
        ),
        height: Math.max(
          MIN_VIEWPORT_HEIGHT,
          drag.startHeight + (growsBottom ? deltaY : 0) - (growsTop ? deltaY : 0),
        ),
      }))
    }
  }

  // Local-first: drag commits the new position + any broken wires into local
  // `nodes` only. Persisted to Firestore later via the Save button.
  const finishNodeDrag = async (drag: Extract<DragState, { type: 'node' }>) => {
    const node = nodesById.get(drag.itemId)
    if (!node) return

    const dropResult = resolveDropPosition(drag.itemId, node.position)
    const nextPosition = dropResult.position
    const displacements = dropResult.displacements

    // Determine which wires break, and fire their animations once (outside the
    // state updater so we don't run side effects during render/update).
    const retainedPrerequisites = node.prerequisites.filter((sourceId) => {
      const source = nodesById.get(sourceId)
      const isValid = source ? source.position.y < nextPosition.y : false
      if (!isValid && source) animateBreakingWire(source, { ...node, position: nextPosition })
      return isValid
    })
    const retainedGateInputIds = (node.gateInputIds ?? []).filter((sourceId) => {
      const source = nodesById.get(sourceId)
      const isValid = source ? source.position.y < nextPosition.y : false
      if (!isValid && source) animateBreakingWire(source, { ...node, position: nextPosition })
      return isValid
    })
    // Detached-child / detached-gate scans must include children on the
    // same book that the current breadcrumb has filtered out. Otherwise a
    // drag that legitimately invalidates a cross-parent dependency would
    // silently leave the stale prereq in place, and the wire would come
    // back the moment the user cleared the breadcrumb filter.
    const detachedChildIds = boardScopedNodes
      .filter((child) => child.prerequisites.includes(node.id) && child.position.y <= nextPosition.y)
      .map((child) => {
        animateBreakingWire({ ...node, position: nextPosition }, child)
        return child.id
      })
    const detachedGateInputIds = boardScopedNodes
      .filter((gate) => isGateNode(gate) && (gate.gateInputIds ?? []).includes(node.id) && gate.position.y <= nextPosition.y)
      .map((gate) => {
        animateBreakingWire({ ...node, position: nextPosition }, gate)
        return gate.id
      })
    const dragVisibleIds = new Set(displayNodes.map((n) => n.id))
    const offCanvasBrokenTitles: string[] = []
    for (const detachedId of [...detachedChildIds, ...detachedGateInputIds]) {
      if (!dragVisibleIds.has(detachedId)) {
        const other = nodesById.get(detachedId)
        offCanvasBrokenTitles.push(other?.title ?? 'an off-canvas item')
      }
    }
    // Reposition may also break INCOMING wires (this node's own prereqs
    // that no longer sit above it). If any of those sources are off-canvas
    // the break is otherwise silent — collect their titles too so the
    // toast reflects the full disconnect.
    for (const sourceId of node.prerequisites) {
      const source = nodesById.get(sourceId)
      if (!source) continue
      if (source.position.y >= nextPosition.y && !dragVisibleIds.has(sourceId)) {
        offCanvasBrokenTitles.push(source.title ?? 'an off-canvas item')
      }
    }

    commitLocalNodes((current) =>
      current.map((candidate) => {
        if (candidate.id === drag.itemId) {
          return { ...candidate, position: nextPosition, prerequisites: retainedPrerequisites, gateInputIds: retainedGateInputIds }
        }
        // Gentle push-to-fit: neighbors that were nudged to make room for
        // this drop land in the same commit so the layout snaps into place
        // in one paint, not a two-step shuffle.
        const nudged = displacements.get(candidate.id)
        if (nudged) {
          return { ...candidate, position: nudged }
        }
        if (detachedChildIds.includes(candidate.id)) {
          return { ...candidate, prerequisites: candidate.prerequisites.filter((id) => id !== node.id) }
        }
        if (detachedGateInputIds.includes(candidate.id)) {
          return { ...candidate, gateInputIds: (candidate.gateInputIds ?? []).filter((id) => id !== node.id) }
        }
        return candidate
      }),
    )
    // Position now lives in `nodes`; clear any live-drag overlay for this
    // node AND for every neighbor we just nudged (so their new position
    // isn't shadowed by a stale optimistic entry).
    setOptimisticPositions((current) => {
      const hasDragged = drag.itemId in current
      let hasNudged = false
      displacements.forEach((_pos, id) => { if (id in current) hasNudged = true })
      if (!hasDragged && !hasNudged) return current
      const next = { ...current }
      delete next[drag.itemId]
      displacements.forEach((_pos, id) => { delete next[id] })
      return next
    })
    if (offCanvasBrokenTitles.length > 0) {
      const [firstTitle, ...rest] = offCanvasBrokenTitles
      const suffix = rest.length > 0 ? ` and ${rest.length} other${rest.length === 1 ? '' : 's'}` : ''
      showFlash(`Disconnected from "${firstTitle}"${suffix} (not visible in current view).`)
    }
  }

  const findAnchorTargetAt = (
    client: { x: number; y: number },
    sourceId: string,
  ) => {
    const board = boardRef.current
    let bestTarget: { node: BoardChecklistItem; distance: number } | null = null

    if (board) {
      // First pass: any drop on a GATE's circular body counts as targeting
      // that gate (per spec: "any wire left drawn to and left over the gate
      // will connect it to the gate"). We hit-test the rendered gate disk
      // directly, no anchor matching needed.
      const gateElements = board.querySelectorAll<HTMLElement>('[data-board-gate-id]')
      for (const gateElement of gateElements) {
        const nodeId = gateElement.dataset.boardGateId
        if (!nodeId || nodeId === sourceId) continue
        const node = nodesById.get(nodeId)
        if (!node) continue
        const rect = gateElement.getBoundingClientRect()
        const center = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
        const radius = rect.width / 2 + 6 // small slop so edge drops still count
        const distance = Math.hypot(client.x - center.x, client.y - center.y)
        if (distance <= radius && (!bestTarget || distance < bestTarget.distance)) {
          bestTarget = { node, distance }
        }
      }
      if (bestTarget) return bestTarget.node

      const anchorElements = board.querySelectorAll<HTMLButtonElement>('[data-board-anchor-item-id]')
      for (const anchorElement of anchorElements) {
        const nodeId = anchorElement.dataset.boardAnchorItemId
        if (!nodeId || nodeId === sourceId) continue
        const node = nodesById.get(nodeId)
        if (!node) continue
        const rect = anchorElement.getBoundingClientRect()
        const center = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        }
        const distance = Math.hypot(client.x - center.x, client.y - center.y)
        if (distance <= NODE_CAP_DIAMETER) {
          const currentXDist = Math.abs(client.x - center.x)
          const bestXDist = bestTarget && 'centerX' in bestTarget ? Math.abs(client.x - (bestTarget as any).centerX) : Infinity
          if (!bestTarget || distance < bestTarget.distance || currentXDist < bestXDist) {
            bestTarget = { node, distance, centerX: center.x } as any
          }
        }
      }

      if (bestTarget) return bestTarget.node

      // Bubble-body fallback for regular checklists: a drop landing anywhere
      // inside the bubble's rendered rect counts as targeting that node (the
      // wire then visually snaps to its TOP circle via getNodeWireEndpoint).
      // Gates are excluded — they were already matched by the circular pass.
      const bubbleElements = board.querySelectorAll<HTMLElement>('[data-board-bubble-id]')
      for (const bubbleElement of bubbleElements) {
        const nodeId = bubbleElement.dataset.boardBubbleId
        if (!nodeId || nodeId === sourceId) continue
        const node = nodesById.get(nodeId)
        if (!node) continue
        const rect = bubbleElement.getBoundingClientRect()
        if (
          client.x >= rect.left
          && client.x <= rect.right
          && client.y >= rect.top
          && client.y <= rect.bottom
        ) {
          // Distance is 0 inside the rect — first hit wins.
          if (!bestTarget) bestTarget = { node, distance: 0 }
        }
      }
      if (bestTarget) return bestTarget.node
    }

    for (const node of displayNodes) {
      if (node.id === sourceId) continue
      // Side anchors are gone — only top/bottom remain as wire snap points.
      for (const anchor of ['top', 'bottom'] as AnchorName[]) {
        const anchorPoint = getRenderedAnchorPoint(node, anchor)
        const cursor = {
          x: client.x - (board?.getBoundingClientRect().left ?? 0) + (board?.scrollLeft ?? 0),
          y: client.y - (board?.getBoundingClientRect().top ?? 0) + (board?.scrollTop ?? 0),
        }
        const unscaledCursor = { x: cursor.x / boardScale, y: cursor.y / boardScale }
        const distance = Math.hypot(unscaledCursor.x - anchorPoint.x, unscaledCursor.y - anchorPoint.y)
        if (distance <= 36 && (!bestTarget || distance < bestTarget.distance)) {
          bestTarget = { node, distance }
        }
      }
    }

    return bestTarget?.node ?? null
  }

  // Local-first: wire link/unlink mutate local `nodes` only; persisted on Save.
  const finishWireDrag = async (_event: ReactPointerEvent<HTMLDivElement>, drag: Extract<DragState, { type: 'wire' }>) => {
    const source = nodesById.get(drag.sourceId)
    if (!source) return

    const target = findAnchorTargetAt(drag.client, source.id)

    if (!target) {
      commitLocalNodes((current) =>
        current.map((node) =>
          node.prerequisites.includes(source.id)
            ? {
              ...node,
              prerequisites: node.prerequisites.filter((id) => id !== source.id),
              gateInputIds: (node.gateInputIds ?? []).filter((id) => id !== source.id),
            }
            : (node.gateInputIds ?? []).includes(source.id)
              ? { ...node, gateInputIds: (node.gateInputIds ?? []).filter((id) => id !== source.id) }
              : node,
        ),
      )
      showFlash('Disconnected outgoing wire.')
      return
    }

    if (target.id === source.id) {
      showFlash('Choose a different lower node.')
      return
    }

    if (target.position.y <= source.position.y) {
      showFlash('Move the target below the source before linking.')
      return
    }

    // Branch on node kinds:
    //   regular  → gate    : append source to target.gateInputIds (max 4)
    //   gate     → regular : append gate id to target.prerequisites (so the
    //                        gate's AND/OR semantic kicks in via the lock logic)
    //   regular  → regular : append source to target.prerequisites (existing)
    //   gate     → gate    : disallowed (one-level gating per spec)
    const sourceIsGate = source.nodeKind === 'GATE_AND' || source.nodeKind === 'GATE_OR'
    const targetIsGate = target.nodeKind === 'GATE_AND' || target.nodeKind === 'GATE_OR'

    if (sourceIsGate && targetIsGate) {
      showFlash('Gates cannot connect to other gates.')
      return
    }

    if (targetIsGate) {
      const currentInputs = target.gateInputIds ?? []
      if (currentInputs.includes(source.id)) return
      if (currentInputs.length >= 4) {
        showFlash('Gate already has the maximum 4 inputs.')
        return
      }
      commitLocalNodes((current) =>
        current.map((node) =>
          node.id === target.id
            ? { ...node, gateInputIds: [...(node.gateInputIds ?? []), source.id] }
            : node,
        ),
      )
      return
    }

    assertAndSemanticsForPrereqs(source.id, target)
    commitLocalNodes((current) =>
      current.map((node) =>
        node.id === target.id && !node.prerequisites.includes(source.id)
          ? { ...node, prerequisites: [...node.prerequisites, source.id] }
          : node,
      ),
    )
  }

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    dragRef.current = null
    setWireDraft(null)

    if (!drag) return

    if (drag.type === 'pan') {
      // Fling-style momentum: take the last ~100ms of velocity samples, run a
      // rAF decay loop that continues scrolling until it falls below 0.2 px/frame.
      const samples = panVelocityRef.current
      panVelocityRef.current = []
      if (samples.length >= 2) {
        const now = performance.now()
        const recent = samples.filter((sample) => now - sample.t < 120)
        if (recent.length >= 2) {
          const first = recent[0]
          const last = recent[recent.length - 1]
          const dt = Math.max(1, last.t - first.t)
          let vx = ((last.x - first.x) / dt) * 16
          let vy = ((last.y - first.y) / dt) * 16
          const board = boardRef.current
          if (board && (Math.abs(vx) > 0.5 || Math.abs(vy) > 0.5)) {
            const decay = 0.92
            const maxFrames = 120
            let frames = 0
            const tick = () => {
              if (frames++ > maxFrames || (Math.abs(vx) < 0.2 && Math.abs(vy) < 0.2)) {
                panMomentumRafRef.current = 0
                return
              }
              board.scrollLeft -= vx
              board.scrollTop -= vy
              vx *= decay
              vy *= decay
              panMomentumRafRef.current = requestAnimationFrame(tick)
            }
            panMomentumRafRef.current = requestAnimationFrame(tick)
          }
        }
      }
      return
    }

    if (drag.type === 'node') {
      if (drag.didMove) {
        // Canvas → Visionboard shelf: if the pointer is released over the
        // shelf dropzone, treat the drag as a move to the visionboard list
        // instead of repositioning. The node disappears from the canvas and
        // a record is added to the shelf source (session-local).
        const droppedNode = nodesById.get(drag.itemId)
        if (droppedNode && isCalendarShelfOpen) {
          const dropEl = document.elementFromPoint(event.clientX, event.clientY) as Element | null
          if (dropEl?.closest('[data-visionboard-drop-zone="true"]')) {
            void moveNodeToVisionboard(droppedNode)
            return
          }
        }
        void finishNodeDrag(drag)
      } else {
        // Gates are a separate category — they only govern flow and never
        // expose the node detail drawer. A bare tap on a gate is a no-op
        // (drag moves the gate; the dedicated X deletes it when open).
        const tappedNode = nodesById.get(drag.itemId)
        if (tappedNode && !isGateNode(tappedNode)) {
          setSelectedNodeId(drag.itemId)
        }
      }
    }
    if (drag.type === 'wire') void finishWireDrag(event, drag)
  }


  return {
    handleBoardPointerDown,
    handleNodePointerDown,
    handleAnchorPointerDown,
    handleBoardNodeMouseEnter,
    handleBoardNodeMouseMove,
    handleBoardNodeTouchStart,
    clearBoardNodeTooltip,
    deleteGateRequest,
    deleteOpenGate,
    handlePointerMove,
    handlePointerUp,
  }
}
